import { useState, useEffect } from 'react';
import { User, ADMIN_EMAIL } from '../types/dashboard';
import DashboardOverview from './DashboardOverview';
import DashboardKeys from './DashboardKeys';
import DashboardAnalyticsView from './DashboardAnalyticsView';
import DashboardInsightsView from './DashboardInsightsView';
import DashboardActivityView from './DashboardActivityView';
import DashboardLogsView from './DashboardLogsView';
import DashboardMocksView from './DashboardMocksView';
import DashboardAdminView from './DashboardAdminView';

interface DashboardProps {
  onBack: () => void;
}

type DashboardView = 'overview' | 'analytics' | 'insights' | 'activity' | 'logs' | 'keys' | 'mocks' | 'admin';

export default function Dashboard({ onBack }: DashboardProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState<DashboardView>('overview');

  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = async () => {
    try {
      const response = await fetch('/api/auth/session');
      const data = await response.json();

      if (data.user) {
        setUser(data.user);
      } else {
        // Redirect to sign in if not authenticated
        window.location.href = '/?view=signin';
      }
    } catch (error) {
      console.error('Session check failed:', error);
      window.location.href = '/?view=signin';
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-coral-500 border-t-transparent"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="text-gray-500 hover:text-gray-700 transition-colors"
                title="Back to home"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-coral-600 to-teal-600 bg-clip-text text-transparent">
                JsonToonCraft
              </h1>
            </div>

            {/* User menu */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                {user.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={user.name}
                    className="w-10 h-10 rounded-full border-2 border-coral-200"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-coral-500 to-teal-500 flex items-center justify-center text-white font-semibold">
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="hidden sm:block">
                  <p className="text-sm font-medium text-gray-900">{user.name}</p>
                  <p className="text-xs text-gray-500">{user.email}</p>
                </div>
              </div>

              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <nav className="bg-white rounded-2xl shadow-sm border border-gray-200 p-2 sticky top-8">
              {/* Overview */}
              <button
                onClick={() => setCurrentView('overview')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  currentView === 'overview'
                    ? 'bg-gradient-to-r from-coral-500 to-teal-500 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <span className="font-medium">Overview</span>
              </button>

              {/* Analytics */}
              <button
                onClick={() => setCurrentView('analytics')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors mt-1 ${
                  currentView === 'analytics'
                    ? 'bg-gradient-to-r from-coral-500 to-teal-500 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span className="font-medium">Analytics</span>
              </button>

              {/* Insights */}
              <button
                onClick={() => setCurrentView('insights')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors mt-1 ${
                  currentView === 'insights'
                    ? 'bg-gradient-to-r from-coral-500 to-teal-500 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <span className="font-medium">Insights</span>
              </button>

              {/* Activity */}
              <button
                onClick={() => setCurrentView('activity')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors mt-1 ${
                  currentView === 'activity'
                    ? 'bg-gradient-to-r from-coral-500 to-teal-500 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="font-medium">Activity</span>
              </button>

              {/* API Logs */}
              <button
                onClick={() => setCurrentView('logs')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors mt-1 ${
                  currentView === 'logs'
                    ? 'bg-gradient-to-r from-coral-500 to-teal-500 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="font-medium">API Logs</span>
              </button>

              {/* Divider */}
              <div className="my-2 border-t border-gray-200"></div>

              {/* Mock APIs */}
              <button
                onClick={() => setCurrentView('mocks')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  currentView === 'mocks'
                    ? 'bg-gradient-to-r from-coral-500 to-teal-500 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
                <span className="font-medium">Mock APIs</span>
              </button>

              {/* API Keys */}
              <button
                onClick={() => setCurrentView('keys')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  currentView === 'keys'
                    ? 'bg-gradient-to-r from-coral-500 to-teal-500 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                <span className="font-medium">API Keys</span>
              </button>

              {/* Admin Panel - Only visible for admin user */}
              {user?.email === ADMIN_EMAIL && (
                <>
                  <div className="my-2 border-t border-gray-200"></div>
                  <button
                    onClick={() => setCurrentView('admin')}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                      currentView === 'admin'
                        ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white'
                        : 'text-purple-700 hover:bg-purple-50'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <span className="font-medium">Admin Panel</span>
                  </button>
                </>
              )}
            </nav>
          </aside>

          {/* Main content */}
          <main className="flex-1">
            {currentView === 'overview' && <DashboardOverview userId={user.id} />}
            {currentView === 'analytics' && <DashboardAnalyticsView userId={user.id} />}
            {currentView === 'insights' && <DashboardInsightsView userId={user.id} />}
            {currentView === 'activity' && <DashboardActivityView userId={user.id} />}
            {currentView === 'logs' && <DashboardLogsView userId={user.id} />}
            {currentView === 'mocks' && <DashboardMocksView userId={user.id} />}
            {currentView === 'keys' && <DashboardKeys />}
            {currentView === 'admin' && user?.email === ADMIN_EMAIL && <DashboardAdminView userId={user.id} />}
          </main>
        </div>
      </div>
    </div>
  );
}
